import { z } from 'zod'
import { DOCUMENT_STATUS } from '../enums/index.js'
import { LINE_SECTIONS } from '../documents/types.js'

/** A decimal string, not a float. Rejects anything a numeric column would. */
export const decimalString = (maxDp: number) =>
  z.string().regex(new RegExp(`^-?\\d{1,15}(\\.\\d{1,${maxDp}})?$`), `expected a decimal with at most ${maxDp} places`)

export const moneySchema = decimalString(4)
export const qtySchema = decimalString(3)
export const rateSchema = decimalString(4)
export const pctSchema = decimalString(4)

/**
 * A line grid shows only the columns its document type has: a tool issue has no
 * rate, a stock count has no price, and a half-filled row leaves the box empty.
 * Anything the grid does not show reaches the server blank, so these settle at
 * zero rather than refusing the save. What a given type insists on is a rule,
 * checked at submit, not a wire format.
 */
const blankAsZero = (schema: z.ZodType<string>): z.ZodType<string, z.ZodTypeDef, string | null | undefined> =>
  z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? '0' : value),
    schema,
    // The cast states what preprocess erases: blank in, a decimal out.
  ) as unknown as z.ZodType<string, z.ZodTypeDef, string | null | undefined>

/**
 * An id cell that may be left blank. A cleared type-ahead sends '' and a grid
 * that has no such column sends nothing at all; both mean "not set" rather than
 * "not a uuid", so they settle to undefined instead of refusing the save.
 */
const optionalId = z.preprocess(
  (v) => (v === '' || v === null ? undefined : v),
  z.string().uuid().optional(),
) as unknown as z.ZodType<string | undefined, z.ZodTypeDef, string | null | undefined>

/** A saved row's id, or the `seq:BOQ:3` key of one being written alongside it. */
const optionalLineRef = z.preprocess(
  (v) => (v === '' || v === null ? undefined : v),
  z.union([z.string().uuid(), z.string().regex(/^seq:[A-Z_]+:\d+$/)]).optional(),
) as unknown as z.ZodType<string | undefined, z.ZodTypeDef, string | null | undefined>

const yesNo = z.enum(['YES', 'NO']).nullish()

export const documentLineSchema = z.object({
  id: z.string().uuid().optional(),
  seq: z.number().int().positive(),
  /** Which grid on the document this row belongs to. Defaults to the first. */
  section: z.enum(LINE_SECTIONS).optional(),
  itemId: z.string().uuid().nullable().optional(),
  // A picked row with no description or unit sends null for it; that means
  // "not given", as a missing key does, not a reason to refuse the save.
  description: z.string().max(2000).nullish().transform((v) => v ?? undefined),
  brand: z.string().max(120).nullable().optional(),
  quantity: blankAsZero(qtySchema),
  unit: z.string().max(24).nullish().transform((v) => v ?? undefined),
  rate: blankAsZero(rateSchema),
  discountPct: blankAsZero(pctSchema).optional(),
  /** Server-computed. Accepted on input but always recalculated. */
  amount: moneySchema.optional(),
  /** For allocating documents: which source line this consumes. */
  fromLineId: z.string().uuid().nullable().optional(),

  /* -- the BOQ and the estimate under it (docs/05, docs/13) -------------- */
  /** A BOQ row is either a heading or a priced item. */
  lineType: z.enum(['SECTION', 'ITEM']).nullish(),
  /** The customer's own reference: 12.1, 86 - 1.a. */
  boqCode: z.string().max(60).nullish(),
  clientRef: z.string().max(60).nullish(),
  /**
   * An estimation line hangs off the BOQ line it costs.
   *
   * Either the parent's id, or — for rows arriving together, as a BOQ import
   * does — the temporary key `seq:<SECTION>:<n>` of a row in the same save.
   * The save path resolves those to real ids once both rows exist.
   */
  parentLineId: optionalLineRef,
  /** What engineering says is needed, as against what was ordered. */
  designQuantity: blankAsZero(qtySchema).optional(),
  supplyRate: blankAsZero(rateSchema).optional(),
  /** How much of this goes into one unit of the BOQ line above it. */
  quantityMultiplier: blankAsZero(qtySchema).optional(),
  costType: z.enum(['MATERIAL', 'LABOUR', 'MATERIAL_AND_LABOUR', 'BUNDLED', 'LUMPSUM', 'EXPENSE']).nullish(),
  /** A priced line with no fixed quantity. Nothing can exceed it. */
  rateOnly: yesNo,
  /** NO marks extra work outside the original tender. */
  tendered: yesNo,
  isBillable: yesNo,
  allowedBrands: z.string().max(500).nullish(),
  hsnSacCode: z.string().max(20).nullish(),

  /* -- billing terms ----------------------------------------------------- */
  termName: z.string().max(120).nullish(),
  termSection: z.enum(['SUPPLY', 'INSTALLATION']).nullish(),
  percentage: blankAsZero(pctSchema).optional(),
  linkedTo: z.enum(['NONE', 'SUPPLY', 'INSTALLATION']).nullish(),
  /** Which billing term line a BOQ line, or a billed line, sits under. */
  billingTermLineId: optionalId,

  /* -- everything else a grid may carry ---------------------------------- */
  supplierId: optionalId,
  forProjectId: optionalId,
  toolUnitId: optionalId,
  supplierBillingTermId: optionalId,
  warehouseId: optionalId,
  fromReserved: blankAsZero(qtySchema).optional(),
  direction: z.enum(['DEDUCTION', 'ADDITION']).nullish(),
  decision: z.enum(['', 'AWARDED', 'REJECTED']).nullish(),
})
  /**
   * A document type may invent its own line columns — 'BOQ no' on a project,
   * 'Billing qty' on a billing request, 'Identifier(s)' on a tool entry. They
   * have no column of their own and ride in the line's attrs, so the wire
   * accepts them rather than dropping what the operator typed.
   */
  .catchall(z.union([z.string().max(1000), z.number(), z.boolean(), z.null()]).optional())
export type DocumentLineInput = z.infer<typeof documentLineSchema>

export const saveDocumentSchema = z.object({
  /** Optimistic lock. Omitted only on create. */
  version: z.number().int().nonnegative().optional(),
  fields: z.record(z.unknown()),
  lines: z.array(documentLineSchema).default([]),
})
export type SaveDocumentInput = z.infer<typeof saveDocumentSchema>

export const createDocumentSchema = z.object({ fields: z.record(z.unknown()) })
export type CreateDocumentInput = z.infer<typeof createDocumentSchema>

export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: z.enum(DOCUMENT_STATUS).optional(),
  q: z.string().max(200).optional(),
  projectId: z.string().uuid().optional(),
  supplierId: z.string().uuid().optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
})
  /**
   * Every other filter a definition declares: Customer, Job type, Tax unit,
   * Warehouse, Submitted by, Progress, Issued to… The server checks each against
   * the definition and applies it. Stripping them here is how seventeen lists
   * came to show a filter as applied and return every row.
   */
  .passthrough()
export type ListQuery = z.infer<typeof listQuerySchema>

/** One violation raised by the rules engine, rendered as a table in the UI. */
export const violationSchema = z.object({
  rule: z.string(),
  severity: z.enum(['BLOCK', 'OVERRIDE', 'WARN', 'INFO']),
  title: z.string(),
  explanation: z.string(),
  columns: z.array(z.string()),
  rows: z.array(z.array(z.string())),
})
export type Violation = z.infer<typeof violationSchema>

export const submitResultSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), documentStatus: z.enum(DOCUMENT_STATUS), withExceptions: z.boolean() }),
  z.object({ ok: z.literal(false), violations: z.array(violationSchema) }),
])
export type SubmitResult = z.infer<typeof submitResultSchema>
